import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Form, Button, Col, Row, Input, Tag as TagAntd, Space } from 'antd';

import { useAppDispatch, useAppSelector } from 'src/hooks/store';
import { Tag, fetchAllTags } from 'src/slices/tag';
import { createUseStyles } from 'react-jss';
import { tasks } from '@microsoft/teams-js';
import { useTeamsFx } from 'src/utils/useTeamsFx';
import { fetchCurrentTeamSettings } from 'src/slices/auth';
import { tagsSelector } from '../store';

const { CheckableTag } = TagAntd;

const useStyles = createUseStyles({
  formLayout: {
    padding: '5%',
  },
  checkableTag: {
    fontSize: 'medium',
    padding: '2px 5px !important',
  },
  actionsButtons: {
    float: 'right',
  },
});

const AddArticleBot: React.VFC = () => {
  const classes = useStyles();

  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { context } = useTeamsFx();

  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);

  const [formArticle] = Form.useForm();
  const [formUrl] = Form.useForm();

  const { tags, isLoading } = useAppSelector((state) => ({
    tags: tagsSelector.selectAll(state),
    isLoading: state.tag.isLoading,
  }));

  useEffect(() => {
    if (context?.teamId) {
      const fetchSettings = async () => {
        try {
          await dispatch(fetchAllTags(context.teamId || ''));
          await dispatch(
            fetchCurrentTeamSettings(context.teamId || ''),
          ).unwrap();
        } catch (error) {}
      };

      fetchSettings();
    }
  }, [context?.teamId]);

  const sendForm = (formResult: { summary: string; comment?: string }) => {
    const selectedTagsIds = tags
      .filter((tag) =>
        selectedTags.find((selectedTag) => tag._id === selectedTag._id),
      )
      .map((tag) => tag._id);

    tasks.submitTask({ ...formResult, selectedTagsIds: selectedTagsIds });
    resetForm();
  };

  const resetForm = () => {
    formArticle.resetFields();
    formUrl.resetFields();
  };

  const cancelForm = () => {
    resetForm();
    tasks.submitTask();
  };

  const checkTag = (tag: Tag, checked: boolean) => {
    setSelectedTags(
      checked ? [...selectedTags, tag] : selectedTags.filter((t) => t !== tag),
    );
  };

  return (
    <div className={classes.formLayout}>
      {!isLoading && (
        <Form layout="vertical" onFinish={sendForm} form={formArticle}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="selectedTags"
                label={t('addArticleTranslation.selectTags')}
                rules={[
                  {
                    required: selectedTags.length === 0,
                    message: t('addArticleTranslation.errorSelectTags'),
                  },
                ]}
              >
                <Space size={[8, 10]} wrap className="tagsList">
                  {tags.map((tag) => {
                    const isChecked =
                      selectedTags.find(
                        (tagToFind) => tagToFind._id === tag._id,
                      ) !== undefined;

                    return (
                      <CheckableTag
                        key={tag._id}
                        checked={isChecked}
                        onChange={(checked) => checkTag(tag, checked)}
                        className={`${isChecked ? '' : 'ant-tag-processing'} ${
                          classes.checkableTag
                        }`}
                      >
                        {`#${tag.name}`}
                      </CheckableTag>
                    );
                  })}
                </Space>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="summary"
                label={t('addArticleTranslation.summaryText')}
                rules={[
                  {
                    message: t('addArticleTranslation.summaryInput'),
                  },
                ]}
              >
                <Input.TextArea
                  rows={4}
                  placeholder={t('addArticleTranslation.summaryInput')}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="comment"
                label={t('addArticleTranslation.commentText')}
              >
                <Input.TextArea
                  rows={4}
                  placeholder={t('addArticleTranslation.commentInput')}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Space className={classes.actionsButtons}>
                <Button onClick={() => cancelForm()}>
                  {t('common.cancelBtn')}
                </Button>
                <Button type="primary" htmlType="submit">
                  {t('common.addBtn')}
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      )}
    </div>
  );
};

export default AddArticleBot;
